import type { CurrencyMetadataRow } from '@/types';

/* ---------------------------------------------------------------------------
   Money
   MySQL stores amounts as decimal(38,18) and knex hands them over as strings:
   "1.400000000000000000". Nobody can read that. currency_metadata exists
   precisely to fix it: symbol "€", display_precision 2, rounding ROUND_DOWN.
   We format for humans and keep the exact string for the tooltip — support
   sometimes needs the true value, so we never destroy it.
   --------------------------------------------------------------------------- */

export function formatMoney(
  amount: string | number | null | undefined,
  meta?: CurrencyMetadataRow | null,
  currencyFallback?: string | null,
): string {
  if (amount === null || amount === undefined || amount === '') return '—';

  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);

  const precision = meta?.display_precision ?? 2;
  const rounded = roundBy(n, precision, meta?.rounding);
  const body = rounded.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  const symbol = meta?.symbol ?? currencyFallback ?? '';
  if (!symbol) return body;
  // Symbols lead; bare currency codes trail (€1.40 vs 1.40 XYZ).
  return symbol.length <= 2 ? `${symbol}${body}` : `${body} ${symbol}`;
}

/** Honour currency_metadata.rounding rather than assuming banker's/half-up. */
function roundBy(n: number, precision: number, rounding?: string): number {
  const f = 10 ** precision;
  const scaled = n * f;

  switch (rounding) {
    case 'ROUND_UP':
      return (n >= 0 ? Math.ceil(scaled) : Math.floor(scaled)) / f;
    case 'ROUND_DOWN':
      return Math.trunc(scaled) / f;
    case 'ROUND_CEIL':
      return Math.ceil(scaled) / f;
    case 'ROUND_FLOOR':
      return Math.floor(scaled) / f;
    case 'ROUND_HALF_DOWN':
      return -Math.round(-scaled) / f;
    case 'ROUND_HALF_EVEN': {
      const r = Math.round(scaled);
      const isHalf = Math.abs(scaled % 1) === 0.5;
      return (isHalf && r % 2 !== 0 ? r - Math.sign(scaled) : r) / f;
    }
    default: // ROUND_HALF_UP
      return Math.round(scaled) / f;
  }
}

/**
 * Coefficients/odds/multipliers are also decimal(38,18) but are NOT money, so
 * they never pass through currency_metadata. "1.780000000000000000" is still
 * unreadable though — trim to the significant part.
 */
export function formatDecimal(value: unknown, maxDp = 6): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return String(Number(n.toFixed(maxDp)));
}

/** Epoch milliseconds stored as a bare number (e.g. crash cashout_time). */
export function parseEpochMs(value: unknown): Date | null {
  const n = Number(value);
  // Anything below ~2001 is far more likely an id or a count than a timestamp.
  if (!Number.isFinite(n) || n < 1_000_000_000_000 || n > 4_000_000_000_000) return null;
  const d = new Date(n);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Amounts across sources are the same number in different clothes. */
export function amountsEqual(a: unknown, b: unknown): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return String(a) === String(b);
  // decimal(38,18) vs Float64 vs Decimal(38,21) — compare with a tolerance well
  // below any real currency precision.
  return Math.abs(na - nb) < 1e-9;
}

/* ---------------------------------------------------------------------------
   IP
   game_aggr.transactions.player_ip is `int unsigned` (167838234).
   transactions_mv stores the readable string ("10.1.2.26") — and they agree.
   --------------------------------------------------------------------------- */

export function formatIp(ip: number | string | null | undefined): string | null {
  if (ip === null || ip === undefined || ip === '') return null;

  // The mv already gives a dotted quad; pass it through untouched.
  if (typeof ip === 'string' && ip.includes('.')) return ip;

  const n = Number(ip);
  if (!Number.isFinite(n) || n < 0 || n > 0xffffffff) return String(ip);

  return [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
}

/* ---------------------------------------------------------------------------
   Time  ⚠️ the subtle one
   The same event carries different wall-clock values per source: MySQL reports
   the bet at 05:37:38Z while ClickHouse `dt` and round-details say 09:37:38.
   Showing those side by side without comment actively misleads support, so:
     - parse each source with its own convention into a real instant
     - render every timestamp in ONE chosen zone (UTC or local)
     - always keep the raw source string for the tooltip
     - compare instants, never strings
   --------------------------------------------------------------------------- */

export type TimeZoneMode = 'utc' | 'local';

/** MySQL/knex gives an ISO string with Z. */
export function parseMysqlTime(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * ClickHouse `dt` is a naive "YYYY-MM-DD HH:mm:ss" with no zone. The realtime
 * table's column is DateTime('UTC'), so we read it as UTC. If a deployment
 * writes local wall-clock instead, this is the single place to correct it.
 */
export function parseClickhouseTime(value: string | null | undefined): Date | null {
  if (!value) return null;
  const iso = value.trim().replace(' ', 'T');
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatTime(date: Date | null, mode: TimeZoneMode, withDate = false): string {
  if (!date) return '—';

  if (mode === 'utc') {
    const iso = date.toISOString();
    return withDate ? `${iso.slice(0, 10)} ${iso.slice(11, 19)}` : iso.slice(11, 19);
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  if (!withDate) return time;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${time}`;
}

/** Human gap between two instants: "14s", "2m 04s", "1h 03m". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  const abs = Math.abs(Math.round(ms));
  const s = Math.floor(abs / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

/* --------------------------------------------------------------------------- */

/** Long ids/hashes are unreadable in full; keep head+tail, full value on copy. */
export function truncateMiddle(value: string, head = 8, tail = 6): string {
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

export function titleize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
